import type { ApiCareerSnapshot } from "@bridge-applications/pb-types";
import { z } from "zod";

const STORAGE_KEY_PREFIX = "pbtg.chain-offer-auto-open.v1:";
const CachedProgressSchema = z.object({
    schemaVersion: z.literal(1),
    userId: z.uuid(),
    completedFirstJob: z.boolean(),
}).strict();

type CareerProgress = {
    career: Pick<ApiCareerSnapshot["career"], "status">;
    activeJob: Pick<
        NonNullable<ApiCareerSnapshot["activeJob"]>,
        "jobRank" | "status"
    > | null;
};
type ProgressStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): ProgressStorage | null {
    try {
        return typeof window === "undefined" ? null : window.localStorage;
    } catch {
        return null;
    }
}

/** Confirmed progression only: finishing an environment or quest is not a job. */
export function hasCompletedFirstCareerJob(
    snapshot: CareerProgress | null,
): boolean {
    if (snapshot === null) return false;
    return snapshot.career.status === "COMPLETED" ||
        (snapshot.activeJob !== null &&
            (snapshot.activeJob.jobRank > 1 ||
                snapshot.activeJob.status === "COMPLETED"));
}

/** A presentation preference, never payment eligibility or gameplay authority. */
export function cacheChainOfferCareerProgress(
    userId: string | null,
    snapshot: CareerProgress,
    storage: ProgressStorage | null = browserStorage(),
): void {
    const parsed = CachedProgressSchema.safeParse({
        schemaVersion: 1,
        userId,
        completedFirstJob: hasCompletedFirstCareerJob(snapshot),
    });
    if (!parsed.success || storage === null) return;
    try {
        storage.setItem(
            `${STORAGE_KEY_PREFIX}${parsed.data.userId}`,
            JSON.stringify(parsed.data),
        );
    } catch {
        // Confirmed in-memory progress remains usable when storage is blocked.
    }
}

export function canAutoOpenChainOffer(
    userId: string | null,
    snapshotUserId: string | null,
    snapshot: CareerProgress | null,
    storage: ProgressStorage | null = browserStorage(),
): boolean {
    if (userId === null) return false;
    if (snapshot !== null && snapshotUserId === userId) {
        return hasCompletedFirstCareerJob(snapshot);
    }
    if (storage === null) return false;
    try {
        const raw = storage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
        if (raw === null) return false;
        const parsed = CachedProgressSchema.safeParse(JSON.parse(raw));
        return parsed.success && parsed.data.userId === userId &&
            parsed.data.completedFirstJob;
    } catch {
        return false;
    }
}
